import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
    Modal,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    CustomInput,
    Badge
} from 'reactstrap'
import {
    X,
    Clock,
    Check,
    ArrowLeft,
    CheckSquare,
    Square
} from 'react-feather'
import { ArchiveIcon } from '@src/views/components/ArchiveIcons'
import CustomAvatar from '@src/views/components/CustomAvatar'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import { bulkArchiveConversations } from './store/actions'

const InactiveConversationsCleanupModal = ({
    isOpen,
    toggle,
    inactiveData,
    onDismiss,
    onArchiveCompleted
}) => {
    const dispatch = useDispatch()
    const [viewMode, setViewMode] = useState('prompt') // 'prompt' | 'review'
    const [selectedIds, setSelectedIds] = useState([])
    const [isArchiving, setIsArchiving] = useState(false)

    const conversations = inactiveData?.conversations || []
    const count = inactiveData?.count || conversations.length

    // When conversations update or modal opens, default all conversations to selected for review mode
    useEffect(() => {
        if (conversations.length > 0) {
            setSelectedIds(conversations.map(c => c.chat_user))
        }
    }, [conversations])

    // Reset to prompt mode whenever modal is reopened
    useEffect(() => {
        if (isOpen) {
            setViewMode('prompt')
            setIsArchiving(false)
        }
    }, [isOpen])

    const handleToggleSelectAll = () => {
        if (selectedIds.length === conversations.length) {
            setSelectedIds([])
        } else {
            setSelectedIds(conversations.map(c => c.chat_user))
        }
    }

    const handleToggleIndividual = (userId) => {
        if (selectedIds.includes(userId)) {
            setSelectedIds(selectedIds.filter(id => id !== userId))
        } else {
            setSelectedIds([...selectedIds, userId])
        }
    }

    // Execute bulk archive for all inactive conversations
    const handleArchiveAll = () => {
        if (isArchiving || conversations.length === 0) return
        const allIds = conversations.map(c => c.chat_user)
        setIsArchiving(true)

        dispatch(bulkArchiveConversations(allIds))
            .then(res => {
                setIsArchiving(false)
                const archivedCount = res?.data?.archived_count || allIds.length
                toast.success(
                    <div className='d-flex align-items-center'>
                        <ArchiveIcon size={18} className='mr-75 flex-shrink-0' style={{ color: '#1e2d4d' }} />
                        <div>
                            <div className='font-weight-bold'>Inbox Cleaned</div>
                            <small>{archivedCount} conversation{archivedCount > 1 ? 's' : ''} moved to Archive.</small>
                        </div>
                    </div>,
                    { autoClose: 3500 }
                )
                if (onArchiveCompleted) {
                    onArchiveCompleted(archivedCount, allIds)
                }
            })
            .catch(() => {
                setIsArchiving(false)
                toast.error('Failed to move conversations to Archive. Please try again.')
            })
    }

    // Execute bulk archive for selected conversations in review mode
    const handleArchiveSelected = () => {
        if (isArchiving || selectedIds.length === 0) return
        setIsArchiving(true)

        dispatch(bulkArchiveConversations(selectedIds))
            .then(res => {
                setIsArchiving(false)
                const archivedCount = res?.data?.archived_count || selectedIds.length
                toast.success(
                    <div className='d-flex align-items-center'>
                        <ArchiveIcon size={18} className='mr-75 flex-shrink-0' style={{ color: '#1e2d4d' }} />
                        <div>
                            <div className='font-weight-bold'>Conversations Archived</div>
                            <small>{archivedCount} conversation{archivedCount > 1 ? 's' : ''} moved to Archive.</small>
                        </div>
                    </div>,
                    { autoClose: 3500 }
                )
                if (onArchiveCompleted) {
                    onArchiveCompleted(archivedCount, selectedIds)
                }
            })
            .catch(() => {
                setIsArchiving(false)
                toast.error('Failed to move selected conversations to Archive. Please try again.')
            })
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={onDismiss}
            className="modal-dialog-centered cleanup-modal"
            contentClassName="cleanup-modal-content border-0 shadow-lg"
            backdrop="static"
            fade={true}
        >
            {/* Modern Modal Header matching Messages Theme */}
            <div className="cleanup-modal-header d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center flex-grow-1 mr-1 overflow-hidden">
                    {viewMode === 'review' ? (
                        <button
                            type="button"
                            className="cleanup-back-btn mr-1 flex-shrink-0"
                            onClick={() => setViewMode('prompt')}
                            title="Back to Overview"
                            aria-label="Back to overview"
                        >
                            <ArrowLeft size={18} />
                        </button>
                    ) : (
                        <div className="cleanup-icon-badge mr-1 flex-shrink-0">
                            <ArchiveIcon size={20} />
                        </div>
                    )}
                    <div className="cleanup-header-text flex-grow-1 overflow-hidden">
                        <div className="d-flex align-items-center flex-wrap">
                            <h5 className="mb-0 font-weight-bold cleanup-title">
                                {viewMode === 'review' ? 'Review Inactive Conversations' : 'Inbox Cleanup'}
                            </h5>
                            {viewMode === 'prompt' ? (
                                <span className="cleanup-header-pill ml-75">
                                    <span className="cleanup-pill-dot mr-50" />
                                    {count} Inactive
                                </span>
                            ) : (
                                <span className="cleanup-header-pill ml-75">
                                    {selectedIds.length} of {conversations.length} Selected
                                </span>
                            )}
                        </div>
                        <p className="mb-0 cleanup-subtitle text-truncate">
                            {viewMode === 'review'
                                ? 'Select the conversations you want to move to Archive'
                                : 'Keep your Messages focused and clutter-free'}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    className="cleanup-close-btn flex-shrink-0"
                    onClick={onDismiss}
                    aria-label="Close"
                    title="Close"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Modal Body */}
            <ModalBody className="p-2">
                {viewMode === 'prompt' ? (
                    <div className="cleanup-prompt-view text-center py-50">
                        {/* Main Question */}
                        <h4 className="cleanup-question font-weight-bold mb-1 px-50">
                            “You have {count} conversations with no activity in over 6 months. Want to move them to Archive?”
                        </h4>

                        {/* Explanation matching theme */}
                        <p className="cleanup-explanation text-muted px-75 mb-0">
                            Moving them to Archive keeps your Inbox organized without losing any message history.
                            You can easily view or restore them at any time from your <strong>Archive</strong> tab.
                        </p>
                    </div>
                ) : (
                    <div className="cleanup-review-view">
                        {/* Select All Bar */}
                        <div className="d-flex align-items-center justify-content-between p-75 mb-1 rounded cleanup-select-all-bar">
                            <div
                                className="d-flex align-items-center cursor-pointer select-none"
                                onClick={handleToggleSelectAll}
                            >
                                <CustomInput
                                    type="checkbox"
                                    id="cleanup-select-all-checkbox"
                                    label=""
                                    checked={selectedIds.length === conversations.length && conversations.length > 0}
                                    onChange={handleToggleSelectAll}
                                    className="mr-50"
                                />
                                <span className="font-weight-bold" style={{ fontSize: '13px', color: '#1e2d4d' }}>
                                    Select All ({conversations.length})
                                </span>
                            </div>
                            <span className="cleanup-selected-count-badge">
                                {selectedIds.length} Selected
                            </span>
                        </div>

                        {/* Conversation Cards List */}
                        <div className="cleanup-review-list">
                            {conversations.map((item) => {
                                const isSelected = selectedIds.includes(item.chat_user)
                                return (
                                    <div
                                        key={item.chat_user}
                                        className={`cleanup-conversation-item d-flex align-items-center p-75 mb-75 rounded cursor-pointer ${
                                            isSelected ? 'selected' : ''
                                        }`}
                                        onClick={() => handleToggleIndividual(item.chat_user)}
                                    >
                                        <div className="mr-75 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                            <CustomInput
                                                type="checkbox"
                                                id={`cleanup-check-${item.chat_user}`}
                                                label=""
                                                checked={isSelected}
                                                onChange={() => handleToggleIndividual(item.chat_user)}
                                            />
                                        </div>

                                        <div className="mr-75 flex-shrink-0">
                                            <CustomAvatar
                                                name={item.name}
                                                img={item.profile_image}
                                                imgHeight="38"
                                                imgWidth="38"
                                            />
                                        </div>

                                        <div className="flex-grow-1 overflow-hidden">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center overflow-hidden">
                                                    <span className="font-weight-bold text-truncate mr-50" style={{ fontSize: '13.5px', color: '#1e2d4d' }}>
                                                        {item.name}
                                                    </span>
                                                    <span className="cleanup-role-badge">
                                                        Dietitian
                                                    </span>
                                                </div>
                                                <span className="text-muted flex-shrink-0 ml-50" style={{ fontSize: '11.5px' }}>
                                                    {item.formatted_date}
                                                </span>
                                            </div>

                                            <div className="d-flex align-items-center justify-content-between mt-25">
                                                <div className="text-truncate text-muted" style={{ fontSize: '12.5px', maxWidth: '78%' }}>
                                                    {item.last_message || 'No preview available'}
                                                </div>
                                                <span className="text-muted flex-shrink-0 ml-50 font-weight-500" style={{ fontSize: '11.5px' }}>
                                                    <Clock size={11} className="mr-25 mb-25" />
                                                    {item.months_inactive} mos
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </ModalBody>

            {/* Modal Footer with Actions */}
            <ModalFooter className="cleanup-modal-footer d-flex align-items-center justify-content-between">
                {viewMode === 'prompt' ? (
                    <>
                        <Button
                            color="flat-secondary"
                            onClick={onDismiss}
                            disabled={isArchiving}
                            className="cleanup-btn-dismiss"
                        >
                            Dismiss
                        </Button>
                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <Button
                                onClick={() => setViewMode('review')}
                                disabled={isArchiving}
                                className="cleanup-btn-review"
                            >
                                Review
                            </Button>
                            <Button
                                onClick={handleArchiveAll}
                                disabled={isArchiving}
                                className="cleanup-btn-archive d-flex align-items-center"
                            >
                                {isArchiving ? (
                                    <>
                                        <Spinner size="sm" className="mr-50" />
                                        Archiving...
                                    </>
                                ) : (
                                    <>
                                        <ArchiveIcon size={14} className="mr-50" />
                                        Archive
                                    </>
                                )}
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <Button
                            color="flat-secondary"
                            onClick={() => setViewMode('prompt')}
                            disabled={isArchiving}
                            className="cleanup-btn-dismiss d-flex align-items-center"
                        >
                            <ArrowLeft size={14} className="mr-50" />
                            Back
                        </Button>
                        <Button
                            onClick={handleArchiveSelected}
                            disabled={isArchiving || selectedIds.length === 0}
                            className="cleanup-btn-archive d-flex align-items-center"
                        >
                            {isArchiving ? (
                                <>
                                    <Spinner size="sm" className="mr-50" />
                                    Archiving...
                                </>
                            ) : (
                                <>
                                    <ArchiveIcon size={14} className="mr-50" />
                                    Archive Selected ({selectedIds.length})
                                </>
                            )}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    )
}

InactiveConversationsCleanupModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    inactiveData: PropTypes.shape({
        count: PropTypes.number,
        conversations: PropTypes.arrayOf(PropTypes.object)
    }),
    onDismiss: PropTypes.func.isRequired,
    onArchiveCompleted: PropTypes.func.isRequired
}

export default InactiveConversationsCleanupModal
